import type {
    IBulkHealthCheckRequest,
    IBulkHealthCheckResponse,
    IRelayAgentsResponse,
    IRelayEndpointsResponse,
    IRelayGroupClientConfigDto,
    IRelayGroupMetadata,
    IRelayGroupRegistrationRequestDto,
    IRelayGroupRegistrationResponseDto,
    IRelayGroupResponse,
} from '@experiences/interfaces';

import { mapRelayGroupCollection } from '../mappers/RelayServiceMapper';
import {
    axiosDelete,
    get,
    post,
    put,
} from '../utility/Requests';

export async function getRelayGroups(accountId: string, tenantId: string, top = 25, skip = 0, searchTerm = '') {
    const url = `/${accountId}/${tenantId}/relay_/v1/api/relay-groups`;
    const response = await get<IRelayGroupResponse>(url,
        {
            ignoreBasePath: true,
            urlParams: {
                top,
                skip,
                searchTerm,
            },
        });
    return mapRelayGroupCollection(response);
}

export async function getRelayGroupDetails(accountId: string, tenantId: string, relayGroupId: string) {
    const url = `/${accountId}/${tenantId}/relay_/v1/api/relay-groups/${relayGroupId}`;
    return await get<IRelayGroupMetadata>(url,
        { ignoreBasePath: true });
}

export async function getRelayGroupDetailsWithEndpoints(accountId: string, tenantId: string, relayGroupId: string) {
    const url = `/${accountId}/${tenantId}/relay_/v1/api/relay-groups/${relayGroupId}?include=endpoints`;
    return await get<IRelayGroupRegistrationResponseDto>(url,
        { ignoreBasePath: true });
}

export async function getRelayGroupEndpoints(accountId: string, tenantId: string, relayGroupId: string, top = 10, skip = 0, searchTerm = '') {
    const url = `/${accountId}/${tenantId}/relay_/v1/api/relay-groups/${relayGroupId}/endpoints`;
    return await get<IRelayEndpointsResponse>(url, {
        ignoreBasePath: true,
        urlParams: {
            top,
            skip,
            searchTerm,
        },
    });
}

export async function getRelayGroupAgents(accountId: string, tenantId: string, relayGroupId: string) {
    const url = `/${accountId}/${tenantId}/relay_/v1/api/relay-groups/${relayGroupId}/agents`;
    return await get<IRelayAgentsResponse>(url, { ignoreBasePath: true });
}

export async function deleteRelayGroup(accountId: string, tenantId: string, relayGroupId: string) {
    const url = `/${accountId}/${tenantId}/relay_/v1/api/relay-groups/${relayGroupId}`;
    return await axiosDelete(url, { ignoreBasePath: true });
}

export async function createRelayGroup(accountId: string, tenantId: string, data: IRelayGroupRegistrationRequestDto) {
    const url = `/${accountId}/${tenantId}/relay_/v1/api/relay-groups`;
    return await post<IRelayGroupRegistrationResponseDto>(url,
        {
            body: data,
            ignoreBasePath: true,
        });
}

export async function updateRelayGroup(accountId: string, tenantId: string, relayGroupId: string, data: IRelayGroupRegistrationRequestDto) {
    const url = `/${accountId}/${tenantId}/relay_/v1/api/relay-groups/${relayGroupId}`;
    return await put<IRelayGroupRegistrationResponseDto>(url,
        {
            body: data,
            ignoreBasePath: true,
        });
}

export async function bulkHealthCheck(accountId: string, tenantId: string, groupId: string, request: IBulkHealthCheckRequest) {
    const url = `/${accountId}/${tenantId}/relay_/v1/api/relay-groups/${groupId}/health/check`;
    return await post<IBulkHealthCheckResponse>(url, {
        body: request,
        ignoreBasePath: true,
    });
}

export async function getClientConfig(accountId: string, tenantId: string, relayGroupId: string) {
    const url = `/${accountId}/${tenantId}/relay_/v1/api/relay-groups/${relayGroupId}/client-config`;
    return await get<IRelayGroupClientConfigDto>(url,
        { ignoreBasePath: true });
}

export async function deleteRelayAgent(accountId: string, tenantId: string, agentId: string) {
    const url = `/${accountId}/${tenantId}/relay_/v1/api/relay-agents/${agentId}`;
    return await axiosDelete(url, { ignoreBasePath: true });
}
